"use client";

import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogPanel,
  DialogBackdrop,
} from "@headlessui/react";
import {
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import { UsersIcon } from "@heroicons/react/24/outline";
import { useSearchStore } from "@/app/lib/searchStore";
import { useEffect, useState } from "react";
import AnimalPlaceholder from "./animalPlaceholder";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const speciesMap = {
  Hund: 1,
  Katze: 2,
  Kaninchen: 3,
  Hamster: 4,
  Meerschweinchen: 5,
  Maus: 6,
  Papagei: 7,
  Schlange: 8,
  Schildkröte: 9,
  Fisch: 10,
  Pferd: 11,
  Schaf: 12,
  Kuh: 13,
};

export default function SearchDialog() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const open = useSearchStore((state) => state.open);
  const setOpen = useSearchStore((state) => state.setOpen);

  useEffect(() => {
    if (open) {
      fetch("/api/search")
        .then((res) => res.json())
        .then(setItems)
        .catch(console.error);
    }
  }, [open]);

  const filteredItems =
    query === ""
      ? items
      : items.filter((item) =>
          item.name.toLowerCase().includes(query.toLowerCase())
        );

  return (
    <Dialog
      className="relative z-10"
      open={open}
      onClose={() => {
        setOpen(false);
        setQuery("");
      }}
    >
      <DialogBackdrop
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.2)",
          backdropFilter: "blur(4px)",
        }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
        <DialogPanel
          transition
          className="mx-auto max-w-3xl transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5 transition-all data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        >
          <Combobox
            as="div"
            onChange={(person) => {
              if (person) {
                window.location = person.url;
              }
            }}
          >
            {({ activeOption }) => (
              <>
                <div className="grid grid-cols-1">
                  <ComboboxInput
                    autoFocus
                    className="col-start-1 row-start-1 h-12 w-full pl-11 pr-4 text-base text-gray-900 outline-none placeholder:text-gray-400 sm:text-sm"
                    placeholder="Suchen..."
                    onChange={(event) => setQuery(event.target.value)}
                    onBlur={() => setQuery("")}
                  />
                  <MagnifyingGlassIcon
                    className="pointer-events-none col-start-1 row-start-1 ml-4 size-5 self-center text-gray-400"
                    aria-hidden="true"
                  />
                </div>

                {(query === "" || filteredItems.length > 0) && (
                  <ComboboxOptions
                    as="div"
                    static
                    hold
                    className="flex transform-gpu divide-x divide-gray-100"
                  >
                    <div
                      className={classNames(
                        "max-h-96 min-w-0 flex-auto scroll-py-4 overflow-y-auto px-6 py-4",
                        activeOption && "sm:h-96"
                      )}
                    >
                      {query === "" && (
                        <h2 className="mb-4 mt-2 text-xs font-semibold text-gray-500">
                          Vergeschlagene Suchanfragen
                        </h2>
                      )}
                      <div className="-mx-2 text-sm text-gray-700">
                        {filteredItems.map((item) => (
                          <ComboboxOption
                            as="div"
                            key={item.id}
                            value={item}
                            className="group flex cursor-pointer select-none items-center rounded-md p-2 hover:bg-gray-100"
                          >
                            <span className="ml-3 flex-auto truncate">
                              {item.name}
                              <span className="block text-xs text-gray-500">
                                {item.detail}
                              </span>
                            </span>
                            <ChevronRightIcon className="ml-3 size-5 text-gray-400" />
                          </ComboboxOption>
                        ))}
                      </div>
                    </div>

                    {activeOption && (
                      <div className="hidden h-96 w-1/2 flex-none flex-col divide-y divide-gray-100 overflow-y-auto sm:flex">
                        <div className="flex-none p-6 text-center ">
                          <div className="size-11 mx-auto">
                            <AnimalPlaceholder
                              src=""
                              animalType={speciesMap[activeOption.detail2]}
                            />
                          </div>
                          <h2 className="mt-3 font-semibold text-gray-900">
                            {activeOption.name}
                          </h2>
                          <p className="text-sm/6 text-gray-500">
                            {activeOption.role}
                          </p>
                        </div>
                        <div className="flex flex-auto flex-col justify-between p-6">
                          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm text-gray-700">
                            <dt className="col-end-1 font-semibold text-gray-900">
                              Tierhalter
                            </dt>
                            <dd>{activeOption.detail1}</dd>

                            <dt className="col-end-1 font-semibold text-gray-900">
                              Datum
                            </dt>
                            <dd className="truncate">{activeOption.detail3}</dd>
                            <dt className="col-end-1 font-semibold text-gray-900">
                              URL
                            </dt>
                            <dd className="truncate">
                              <a
                                href={activeOption.url}
                                className="text-indigo-600 underline"
                              >
                                {activeOption.url}
                              </a>
                            </dd>
                          </dl>
                        </div>
                      </div>
                    )}
                  </ComboboxOptions>
                )}

                {query !== "" && filteredItems.length === 0 && (
                  <div className="px-6 py-14 text-center text-sm sm:px-14">
                    <UsersIcon
                      className="mx-auto size-6 text-gray-400"
                      aria-hidden="true"
                    />
                    <p className="mt-4 font-semibold text-gray-900">
                      Keine Ergebnisse gefunden
                    </p>
                    <p className="mt-2 text-gray-500">
                      Wir konnten keine Ergebnisse für "{query}" finden.
                    </p>
                  </div>
                )}
              </>
            )}
          </Combobox>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
